import CircularProgress from "@mui/material/CircularProgress";
import { type CSSProperties, type FormEvent, useState } from "react";
import { useParams } from "react-router-dom";
import { GFG_LOGO } from "@/constants/campaignBuilder";
import {
  REDEEM_EMAIL_PLACEHOLDER,
  REDEEM_STATE_TONE,
  REDEEM_SUBMIT_LABEL,
  REDEEM_SUBTITLE,
  REDEEM_SUPPORT_EMAIL,
  REDEEM_TITLE,
  redeemStateMessage,
} from "@/constants/redeem";
import { useRedeemCodeValid, useSubmitRedeem } from "@/hooks/useRedeem";
import type { RedeemSubmitResult } from "@/interfaces/redeem";

/**
 * Public campaign-specific Redeem URL page (`/redeem/:code`) — the campaign-scoped "Find Your Gift".
 * A recipient enters their email; if it matches a recipient in THIS campaign we resend the original
 * gift email. No gift link or gift info is ever shown — the server answers with a state, which maps
 * to one of four bordered message boxes above the input. Mounted OUTSIDE RequireAuth (the recipient
 * is not a Clerk user); the `:code` is the only credential. Styled like the other public pages
 * (`DeclineGift`) on the shared `gfg-auth-*` card.
 */

const TONE_COLORS = {
  green: { border: "var(--color-good-sage)", background: "var(--color-good-sage-5)" },
  red: { border: "var(--color-good-wine)", background: "var(--color-good-wine-bg)" },
} as const;

function messageBoxStyle(tone: "green" | "red"): CSSProperties {
  const c = TONE_COLORS[tone];
  return {
    border: `1px solid ${c.border}`,
    background: c.background,
    color: "var(--color-rodeo-black)",
    borderRadius: "var(--radius-btn, 6px)",
    padding: "12px 14px",
    margin: "4px 0 16px",
    fontSize: 14,
    lineHeight: 1.5,
    textAlign: "left",
  };
}

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  border: "1px solid var(--color-smoke)",
  borderRadius: "var(--radius-btn, 6px)",
  fontFamily: "var(--font-lato)",
  fontSize: 15,
  marginBottom: 12,
};

export default function RedeemLookup() {
  const { code = "" } = useParams<{ code: string }>();
  const codeQuery = useRedeemCodeValid(code);
  const submit = useSubmitRedeem(code);

  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [result, setResult] = useState<RedeemSubmitResult | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || submit.isPending) return;
    setSubmittedEmail(trimmed);
    try {
      setResult(await submit.mutateAsync(trimmed));
    } catch {
      // Network / unexpected (the code was validated on load, so a 404 here is not expected) — show
      // the generic box rather than an uncaught error.
      setResult({ state: "not_found" });
    }
  }

  const footer = (
    <p className="gfg-auth-hint" style={{ marginTop: 20 }}>
      Need help? Contact{" "}
      <a href={`mailto:${REDEEM_SUPPORT_EMAIL}`} style={{ color: "var(--color-dark-dust)" }}>
        {REDEEM_SUPPORT_EMAIL}
      </a>
    </p>
  );

  return (
    <main className="gfg-auth-screen">
      <div className="gfg-auth-card">
        <img src={GFG_LOGO} alt="Gifts for Good" style={{ height: 34, margin: "0 auto 24px" }} />

        {codeQuery.isLoading && (
          <div style={{ display: "grid", placeItems: "center", padding: "24px 0" }}>
            <CircularProgress aria-label="Loading" sx={{ color: "var(--color-dark-dust)" }} />
          </div>
        )}

        {codeQuery.isError && (
          <>
            <h1 className="gfg-auth-title">{REDEEM_TITLE}</h1>
            <div style={messageBoxStyle("red")} role="alert">
              {redeemStateMessage("not_found", { email: "" })}
            </div>
            {footer}
          </>
        )}

        {codeQuery.isSuccess && (
          <>
            <h1 className="gfg-auth-title">{REDEEM_TITLE}</h1>
            <p className="gfg-auth-sub">{REDEEM_SUBTITLE}</p>

            {result && (
              <div style={messageBoxStyle(REDEEM_STATE_TONE[result.state])} role="status">
                {redeemStateMessage(result.state, {
                  email: submittedEmail,
                  redeemedDate: result.redeemedDate,
                })}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={REDEEM_EMAIL_PLACEHOLDER}
                aria-label="Your email address"
                style={inputStyle}
              />
              <button type="submit" className="gfg-auth-btn" disabled={submit.isPending}>
                {submit.isPending ? (
                  <CircularProgress aria-label="Submitting" size={18} sx={{ color: "white" }} />
                ) : (
                  REDEEM_SUBMIT_LABEL
                )}
              </button>
            </form>
            {footer}
          </>
        )}
      </div>
    </main>
  );
}
